import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { useAppContext } from '@/hooks/useAppContext';
import { Loader2 } from 'lucide-react';

interface CardSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (cardName: string) => void;
  placeholder?: string;
  className?: string;
  filterType?: 'commander' | 'companion' | 'any';
}

export const CardSearchInput = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search cards...",
  className,
  filterType = 'any'
}: CardSearchInputProps) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { config } = useAppContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (value.length < 2) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      setIsLoading(true);
      try {
        let searchQuery = value;

        if (filterType === 'commander') {
          searchQuery = `${value} is:commander`;
        } else if (filterType === 'companion') {
          searchQuery = `${value} is:companion`;
        }

        // Use the search endpoint instead of autocomplete for filtered searches
        const endpoint = filterType === 'any' ? 'autocomplete' : 'search';
        const response = await fetch(`${config.scryfallApiUrl}/cards/${endpoint}?q=${encodeURIComponent(searchQuery)}`, {
          referrerPolicy: 'no-referrer',
        });
        const data = await response.json();

        if (data.object === 'catalog') {
          setSuggestions(data.data);
        } else if (data.object === 'list') {
          // For search endpoint, extract card names
          setSuggestions(data.data.slice(0, 10).map((card: { name: string }) => card.name));
        } else {
          setSuggestions([]);
        }
      } catch {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimeout = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimeout);
  }, [value, config.scryfallApiUrl, filterType]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleSuggestionClick = (suggestion: string) => {
    onSelect(suggestion);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[0]);
    }
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={className}
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>
      {suggestions.length > 0 && (
        <div className="absolute z-10 w-full bg-background border border-input rounded-md mt-1 shadow-lg max-h-60 overflow-y-auto">
          <ul>
            {suggestions.map((suggestion) => (
              <li
                key={suggestion}
                className="px-3 py-2 cursor-pointer hover:bg-accent text-sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSuggestionClick(suggestion);
                }}
              >
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};